import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  FACILITY_LEVELS,
  NUMBER_CODE_PATTERN,
  SESSION_IDLE_MINUTES,
  THEMES,
  type FacilityView,
  type ThemeId,
} from '@wodi/shared';
import {
  Button,
  CardGrid,
  Cell,
  Column,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Section,
  Select,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextField,
  useShortcut,
} from '@wodi/ui';
import { useState } from 'react';

import { setFacilityDefaultTheme } from '../api/appearance.ts';
import { ApiError } from '../api/client.ts';
import {
  createFacility,
  facilitiesQuery,
  organisationKeys,
  organisationQuery,
  updateFacility,
  updateOrganisation,
} from '../api/organisation.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { outsideDialogs, TableScroll } from '../layout/screen.tsx';
import styles from '../patients/patients.module.css';
import { toasts } from '../toasts.ts';
import { SettingsPage } from './settings-nav.tsx';

// Settings > Organisation (PLAN.md Phase 0 "Settings"): the organisation, its facilities
// and branches (name, MFL code, KMHFL level, the code patient and visit numbers carry),
// the idle sign-out, and how dates and times are shown. Only what the API defines; no
// receipt header fields exist yet, so none are shown.

function message(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

const levelOptions = FACILITY_LEVELS.map((level) => ({ id: level, label: `Level ${level}` }));

function OrganisationCard({ online }: { online: boolean }) {
  const queryClient = useQueryClient();
  const organisation = useQuery(organisationQuery);
  const [minutes, setMinutes] = useState<string | null>(null);
  const current = organisation.data?.sessionIdleMinutes;
  const draft = minutes ?? (current === undefined ? '' : String(current));
  const value = Number(draft);
  const valid =
    /^\d+$/.test(draft) && value >= SESSION_IDLE_MINUTES.min && value <= SESSION_IDLE_MINUTES.max;
  const save = useMutation({
    mutationFn: () => updateOrganisation({ sessionIdleMinutes: value }),
    onSuccess: (updated) => {
      queryClient.setQueryData(organisationKeys.organisation, updated);
      setMinutes(null);
      toasts.show({ title: 'Organisation settings saved' });
    },
  });
  return (
    <Section title="Organisation">
      {organisation.isPending ? <LoadingBlock label="Loading the organisation" rows={2} /> : null}
      {organisation.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void organisation.refetch()}>
              Try again
            </Button>
          }
        >
          {message(organisation.error, "The organisation couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {organisation.data === undefined ? null : (
        <>
          <dl className={styles.details}>
            <dt>Name</dt>
            <dd>{organisation.data.name}</dd>
            <dt>Status</dt>
            <dd>{organisation.data.status === 'active' ? 'Active' : 'Suspended'}</dd>
          </dl>
          <form
            className={styles.inlineForm}
            aria-label="Idle sign-out"
            onSubmit={(event) => {
              event.preventDefault();
              if (valid && value !== current) save.mutate();
            }}
          >
            <TextField
              label="Sign out after this many idle minutes"
              description={`Applies to everyone at the next sign-in. ${String(SESSION_IDLE_MINUTES.min)} to ${String(SESSION_IDLE_MINUTES.max)} minutes; 15 suits shared computers.`}
              inputMode="numeric"
              width="xs"
              value={draft}
              onChange={setMinutes}
              {...(valid
                ? {}
                : {
                    errorMessage: `Enter a whole number from ${String(SESSION_IDLE_MINUTES.min)} to ${String(SESSION_IDLE_MINUTES.max)}.`,
                  })}
            />
            {save.error === null ? null : (
              <Notice tone="error">{message(save.error, "It wasn't saved. Try again.")}</Notice>
            )}
            <div className={styles.actions}>
              <Button
                type="submit"
                variant="primary"
                isDisabled={!online || !valid || value === current}
                isPending={save.isPending}
                pendingLabel="Saving…"
              >
                Save idle sign-out
              </Button>
            </div>
          </form>
        </>
      )}
    </Section>
  );
}

const themeOptions = THEMES.map((t) => ({ id: t.id, label: t.label }));

/**
 * The theme each facility shows until a person chooses their own (ADR 0030). Saved as soon
 * as it is chosen. People change their own under Account, whatever is set here.
 */
function DefaultThemeCard({ online }: { online: boolean }) {
  const queryClient = useQueryClient();
  const facilities = useQuery(facilitiesQuery);
  const save = useMutation({
    mutationFn: ({ facilityId, theme }: { facilityId: string; theme: ThemeId }) =>
      setFacilityDefaultTheme(facilityId, theme),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: organisationKeys.facilities });
      // The session carries the theme in effect; someone without their own sees the change.
      await queryClient.invalidateQueries({ queryKey: sessionQuery.queryKey });
      toasts.show({ title: 'Default theme saved' });
    },
  });
  return (
    <Section
      title="Default theme"
      description="How Wodi looks for people at each facility until they choose their own theme under Account."
    >
      {facilities.isPending ? <LoadingBlock label="Loading facilities" rows={1} /> : null}
      {facilities.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void facilities.refetch()}>
              Try again
            </Button>
          }
        >
          {message(facilities.error, "Facilities couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {save.error === null ? null : (
        <Notice tone="error">
          {message(save.error, "The default theme wasn't saved. Try again.")}
        </Notice>
      )}
      {facilities.data?.map((f) => (
        <Select
          key={f.id}
          label={facilities.data.length === 1 ? 'Default theme' : f.name}
          options={themeOptions}
          value={f.defaultTheme}
          width="sm"
          isDisabled={!online || save.isPending}
          onChange={(theme) => {
            if (theme !== null && theme !== f.defaultTheme)
              save.mutate({ facilityId: f.id, theme: theme as ThemeId });
          }}
        />
      ))}
    </Section>
  );
}

function DatesCard() {
  return (
    <Section title="Dates and times">
      <dl className={styles.details}>
        <dt>Time zone</dt>
        <dd>East Africa Time (UTC+3)</dd>
        <dt>Dates</dt>
        <dd>22 Sep 2026</dd>
        <dt>Times</dt>
        <dd>14:05, 24-hour clock</dd>
      </dl>
      <p className={styles.meta}>
        Wodi stores every time in UTC and shows it in East Africa Time on screens and prints. This
        is the same for every facility and isn't a setting.
      </p>
    </Section>
  );
}

interface FacilityDraft {
  name: string;
  mflCode: string;
  level: string | null;
  numberCode: string;
}

function FacilityForm({
  facility,
  online,
  onDone,
}: {
  /** The facility being changed, or undefined to add one. */
  facility?: FacilityView;
  online: boolean;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<FacilityDraft>({
    name: facility?.name ?? '',
    mflCode: facility?.mflCode ?? '',
    level: facility?.level ?? null,
    numberCode: facility?.numberCode ?? '',
  });
  const [tried, setTried] = useState(false);
  const set = (patch: Partial<FacilityDraft>) => {
    setDraft((d) => ({ ...d, ...patch }));
  };
  const code = draft.numberCode.trim();
  const codeValid = code === '' ? facility === undefined : NUMBER_CODE_PATTERN.test(code);
  const valid = draft.name.trim() !== '' && draft.level !== null && codeValid;
  const save = useMutation({
    mutationFn: () => {
      const level = draft.level as FacilityView['level'];
      const common = { name: draft.name.trim(), mflCode: draft.mflCode.trim(), level };
      return facility === undefined
        ? createFacility({ ...common, ...(code === '' ? {} : { numberCode: code }) })
        : updateFacility(facility.id, { ...common, numberCode: code });
    },
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: organisationKeys.facilities });
      await queryClient.invalidateQueries({ queryKey: sessionQuery.queryKey });
      toasts.show({
        title: facility === undefined ? 'Facility added' : 'Facility saved',
        description: saved.name,
      });
      onDone();
    },
  });
  const submit = () => {
    setTried(true);
    if (valid && online) save.mutate();
  };
  useShortcut('mod+enter', submit, {
    allowInFields: true,
    enabled: !save.isPending,
    description: facility === undefined ? 'Add facility' : 'Save facility',
  });
  return (
    <form
      className={styles.inlineForm}
      aria-label={facility === undefined ? 'Add facility' : `Change ${facility.name}`}
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <div className={styles.row}>
        <TextField
          label="Name"
          description="As it appears on prints and to patients."
          value={draft.name}
          onChange={(name) => {
            set({ name });
          }}
          autoFocus
          {...(tried && draft.name.trim() === '' ? { errorMessage: 'Enter a name.' } : {})}
        />
        <Select
          label="KMHFL level"
          placeholder="Choose a level"
          options={levelOptions}
          value={draft.level}
          onChange={(level) => {
            set({ level: level as string });
          }}
          {...(tried && draft.level === null
            ? { errorMessage: 'Choose the level on the Master Facility List.' }
            : {})}
        />
      </div>
      <div className={styles.row}>
        <TextField
          label="MFL code"
          description="Leave it empty until it's confirmed on the Master Facility List."
          width="sm"
          value={draft.mflCode}
          onChange={(mflCode) => {
            set({ mflCode });
          }}
        />
        <TextField
          label="Number code"
          description={
            facility === undefined
              ? 'Shown in patient and visit numbers. Left empty, Wodi uses the next free F1, F2 and so on.'
              : "Shown in patient and visit numbers. Changing it changes new numbers only; numbers already issued don't change."
          }
          width="sm"
          value={draft.numberCode}
          onChange={(numberCode) => {
            set({ numberCode });
          }}
          {...(tried && !codeValid
            ? { errorMessage: 'Use 1 to 10 letters or digits, with no spaces.' }
            : {})}
        />
      </div>
      {save.error === null ? null : (
        <Notice tone="error">{message(save.error, "The facility wasn't saved. Try again.")}</Notice>
      )}
      <div className={styles.actions}>
        <Button
          type="submit"
          variant="primary"
          isDisabled={!online}
          isPending={save.isPending}
          pendingLabel="Saving…"
          shortcut="mod+enter"
        >
          {facility === undefined ? 'Add facility' : 'Save facility'}
        </Button>
        <Button variant="quiet" onPress={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function FacilitiesSection({
  canManage,
  canRead,
  online,
  adding,
  setAdding,
}: {
  canManage: boolean;
  canRead: boolean;
  online: boolean;
  adding: boolean;
  setAdding: (adding: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const facilities = useQuery({ ...facilitiesQuery, enabled: canRead });
  const [editing, setEditing] = useState<FacilityView | null>(null);
  const toggle = useMutation({
    mutationFn: (f: FacilityView) =>
      updateFacility(f.id, { status: f.status === 'active' ? 'inactive' : 'active' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: organisationKeys.facilities });
    },
  });
  return (
    <Section
      title="Facilities and branches"
      description="Each place patients are seen. Number formats are in Patient and visit numbers; the national Facility Registry link is in SHA."
    >
      {!canRead ? (
        <Notice tone="info">
          Seeing the facility list needs permission to see users. Ask an administrator if you need
          it.
        </Notice>
      ) : null}
      {adding ? (
        <FacilityForm
          online={online}
          onDone={() => {
            setAdding(false);
          }}
        />
      ) : null}
      {editing === null ? null : (
        <FacilityForm
          key={editing.id}
          facility={editing}
          online={online}
          onDone={() => {
            setEditing(null);
          }}
        />
      )}
      {facilities.isPending && canRead ? (
        <LoadingBlock label="Loading facilities" rows={3} />
      ) : null}
      {facilities.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void facilities.refetch()}>
              Try again
            </Button>
          }
        >
          {message(facilities.error, "Facilities couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {toggle.error === null ? null : (
        <Notice tone="error">
          {message(toggle.error, "That change wasn't saved. Try again.")}
        </Notice>
      )}
      {facilities.isSuccess ? (
        <TableScroll>
          <Table flat aria-label="Facilities">
            <TableHeader>
              <Column isRowHeader>Name</Column>
              <Column>MFL code</Column>
              <Column>Level</Column>
              <Column>Number code</Column>
              <Column>Status</Column>
              <Column>Actions</Column>
            </TableHeader>
            <TableBody
              items={facilities.data}
              renderEmptyState={() => 'No facilities yet. Add the first one.'}
            >
              {(f) => (
                <Row id={f.id}>
                  <Cell>{f.name}</Cell>
                  <Cell>
                    {f.mflCode === null ? (
                      'Not known yet'
                    ) : (
                      <span className="wodi-id">{f.mflCode}</span>
                    )}
                  </Cell>
                  <Cell>{`Level ${f.level}`}</Cell>
                  <Cell>
                    {f.numberCode === null ? (
                      'None'
                    ) : (
                      <span className="wodi-id">{f.numberCode}</span>
                    )}
                  </Cell>
                  <Cell>
                    <Tag state={f.status === 'active' ? 'active' : 'neutral'}>
                      {f.status === 'active' ? 'Active' : 'Inactive'}
                    </Tag>
                  </Cell>
                  <Cell>
                    {canManage ? (
                      <span className={styles.actions}>
                        <Button
                          dense
                          variant="quiet"
                          isDisabled={!online}
                          aria-label={`Change ${f.name}`}
                          onPress={() => {
                            setAdding(false);
                            setEditing(f);
                          }}
                        >
                          Change
                        </Button>
                        <Button
                          dense
                          variant="quiet"
                          isDisabled={!online || toggle.isPending}
                          aria-label={`${f.status === 'active' ? 'Deactivate' : 'Activate'} ${f.name}`}
                          onPress={() => {
                            toggle.mutate(f);
                          }}
                        >
                          {f.status === 'active' ? 'Deactivate' : 'Activate'}
                        </Button>
                      </span>
                    ) : (
                      'None'
                    )}
                  </Cell>
                </Row>
              )}
            </TableBody>
          </Table>
        </TableScroll>
      ) : null}
      <p className={styles.meta}>
        <Link to="/settings/numbers">Patient and visit numbers</Link>
        {' · '}
        <Link to="/settings/sha">SHA and the Facility Registry</Link>
      </p>
    </Section>
  );
}

export function OrganisationSettingsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const [adding, setAdding] = useState(false);
  const canOrganisation = session !== null && can(session, 'admin.organisation.manage');
  const canFacilities = session !== null && can(session, 'admin.facilities.manage');
  const canReadFacilities = session !== null && can(session, 'admin.users.read');
  const canAdd = canFacilities && canReadFacilities && online && !adding;
  useShortcut(
    'n',
    outsideDialogs(() => {
      setAdding(true);
    }),
    { enabled: canAdd, description: 'Add facility' },
  );

  if (!canOrganisation && !canFacilities) {
    return (
      <>
        <PageHeader title="Organisation" />
        <Notice tone="info">
          You don't have permission to change organisation settings. Ask an administrator if you
          need it.
        </Notice>
      </>
    );
  }

  return (
    <SettingsPage
      title="Organisation"
      {...(canFacilities && canReadFacilities && !adding
        ? {
            actions: (
              <Button
                isDisabled={!online}
                shortcut="n"
                onPress={() => {
                  setAdding(true);
                }}
              >
                Add facility
              </Button>
            ),
          }
        : {})}
    >
      {online ? null : (
        <Notice tone="info">You're offline. Changes can be saved once you reconnect.</Notice>
      )}
      <CardGrid>
        {canOrganisation ? <OrganisationCard online={online} /> : null}
        <DatesCard />
        {canFacilities && canReadFacilities ? <DefaultThemeCard online={online} /> : null}
      </CardGrid>
      {canFacilities || canReadFacilities ? (
        <FacilitiesSection
          canManage={canFacilities}
          canRead={canReadFacilities}
          online={online}
          adding={adding}
          setAdding={setAdding}
        />
      ) : null}
    </SettingsPage>
  );
}
